/**
 * Consolidated Pinia setup file for store initialization
 * All Pinia core functionality is now in pinia-core.js to avoid circular dependencies
 */

import { initializeSearchStore } from '~/stores/useSearchStore'
import { initializeContentStore } from '~/stores/useContentStore'
import { initializeBrandStore } from '@/stores/useBrandStore'
import { initializeCategoryStore } from '~/stores/useCategoryStore'
import { initializeStoreLocatorStore } from '~/stores/useStoreLocatorStore'
import { initializeHomepageStore } from '~/stores/useHomepageStore'
import { initializeStockStore } from '~/stores/useStockStore'
import { initializeStoredCardsStore } from '~/stores/useStoredCardsStore'
import pinia from '~/plugins/pinia-core'

// Re-export from core to maintain API compatibility
export * from '~/plugins/pinia-core'

// SECTION 3: Store Initialization
// ------------------------------

export default ({ app, $axios, store }, inject) => {
  // Make pinia available globally
  app.pinia = pinia
  inject('pinia', pinia)
  app.context.pinia = pinia

  // Initialize stores with Nuxt's axios
  if ($axios) {
    initializeSearchStore($axios)
    initializeContentStore($axios, app.context)
    initializeBrandStore($axios, app.context)
    initializeCategoryStore($axios, app.context)
    initializeStoreLocatorStore($axios)
    initializeHomepageStore($axios)
    initializeStockStore($axios)
    initializeStoredCardsStore({
      $axios,
      $auth: app.$auth,
    })
  }

  try {
    // Initialize root store immediately without delay
    const { useRootStore } = require('~/stores/useRootStore')
    const rootStore = useRootStore()

    // Mirror Vuex loading state to Pinia rootStore
    const unsubscribe = store.subscribe((mutation, state) => {
      if (mutation.type === 'ADD_LOADING') {
        rootStore.addLoading(mutation.payload)
      } else if (mutation.type === 'REMOVE_LOADING') {
        rootStore.removeLoading(mutation.payload)
      } else if (mutation.type === 'ADD_ERROR') {
        rootStore.addError(mutation.payload)
      } else if (mutation.type === 'REMOVE_ERROR') {
        rootStore.removeError(mutation.payload)
      } else if (mutation.type === 'SET_COUPON_CODE') {
        rootStore.setCouponCode(mutation.payload)
      } else if (mutation.type === 'REMOVE_COUPON_CODE') {
        rootStore.removeCouponCode()
      }
    })

    // Cleanup if needed
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        unsubscribe()
      })
    }
  } catch (error) {
    console.error('Error initializing Pinia root store:', error)
  }
}
